import Lang from '@/modules/core/app/services/Lang';
import EnumRelated from '@/modules/core/app/helpers/EnumRelated';
import { DocumentMetaType } from '@/modules/document/constants/DocumentMetaType';
import { DocumentTypeKey } from '@/modules/document/enums/DocumentTypeKey';
import { FileType } from '@/modules/document/enums/FileType';

export class DocumentType extends EnumRelated {
  key!: DocumentTypeKey;
  value!: string;
  color!: string;
  metaType?: string;
  icon?: string;
  description?: string;
  acceptFormats?: string[];

  protected static getFolderValues(): DocumentType[] {
    return Object.values(this).filter((type: DocumentType) => type.icon && type.description);
  }

  static findTypeFromSubTypeKey(subTypeKey?: string): DocumentType {
    const type: DocumentType | undefined = subTypeKey
      ? DocumentType.findValueFromChildTypeKey(subTypeKey)
      : undefined;

    return type ?? DocumentType.OTHERS;
  }

  static AFTERCARE_INSTRUCTIONS_AND_FORMS: DocumentType = {
    key: DocumentTypeKey.AFTERCARE_INSTRUCTIONS_AND_FORMS,
    get value() {
      return Lang.t('document.aftercare-instructions-and-forms');
    },
    color: '#FF8AD7',
    icon: 'checklist-1',
    get description() {
      return Lang.t('document.aftercare-instructions-and-forms-folder-description');
    }
  };

  static ASSET: DocumentType = {
    key: DocumentTypeKey.ASSET,
    get value() {
      return Lang.t('accounting.asset');
    },
    color: '#7752F2',
    icon: 'real-estate-building-house',
    get description() {
      return Lang.t('document.assets-folder-description');
    }
  };

  static BANKING: DocumentType = {
    key: DocumentTypeKey.BANKING,
    get value() {
      return Lang.t('document.banking');
    },
    color: '#1f503a',
    icon: 'saving-bank',
    get description() {
      return Lang.t('document.banking-folder-description');
    }
  };

  static ESTATE: DocumentType = {
    key: DocumentTypeKey.ESTATE,
    get value() {
      return Lang.t('document.estate-documents');
    },
    color: '#61D098',
    icon: 'flower',
    get description() {
      return Lang.t('document.estate-folder-description');
    }
  };

  static IDENTIFICATION: DocumentType = {
    key: DocumentTypeKey.IDENTIFICATION,
    get value() {
      return Lang.t('document.identification');
    },
    color: '#F97068',
    icon: 'single-neutral-actions',
    get description() {
      return Lang.t('document.identification-folder-description');
    }
  };

  static LIABILITY: DocumentType = {
    key: DocumentTypeKey.LIABILITY,
    get value() {
      return Lang.t('accounting.liability');
    },
    color: '#EE964B',
    icon: 'money-bag-dollar',
    get description() {
      return Lang.t('document.liability-folder-description');
    }
  };

  static TAX: DocumentType = {
    key: DocumentTypeKey.TAX,
    get value() {
      return Lang.t('document.tax-and-accounting');
    },
    color: '#D3B246',
    icon: 'accounting-document',
    get description() {
      return Lang.t('document.tax-folder-description');
    }
  };

  static OTHERS: DocumentType = {
    key: DocumentTypeKey.OTHERS,
    get value() {
      return Lang.t('asset.other');
    },
    color: '#7E84A3',
    metaType: DocumentMetaType.DOCUMENT_OTHER_META,
    icon: 'book-flip-page',
    get description() {
      return Lang.t('document.others-folder-description');
    }
  };

  static BILLING: DocumentType = {
    key: DocumentTypeKey.BILLING,
    get value() {
      return Lang.t('billing.billing');
    },
    color: '#97571e',
    metaType: DocumentMetaType.DOCUMENT_OTHER_META
  };

  static INTERNAL_DOCUMENT: DocumentType = {
    key: DocumentTypeKey.INTERNAL_DOCUMENT,
    get value() {
      return Lang.t('document.internal');
    },
    color: '#919297',
    icon: 'content-paper-edit',
    get description() {
      return Lang.t('document.internal-folder-description');
    },
    acceptFormats: [
      ...FileType.getStandardDocumentAcceptTypes(),
      FileType.DOC.key,
      FileType.DOCX.key
    ]
  };

  static COLLABORATORS: DocumentType = {
    key: DocumentTypeKey.COLLABORATORS,
    get value() {
      return Lang.t('document.collaborators');
    },
    color: '#FC075F',
    icon: 'multiple-man-woman-2',
    get description() {
      return Lang.t('document.collaborators-folder-description');
    }
  };
}
